import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import serviceAreaContentService from '../../../services/serviceAreaContentService';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    serviceArea: 'Zonnepanelen',
    author: 'BVM Group Redactie',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [filterServiceArea, setFilterServiceArea] = useState('all');

  const serviceAreas = [
    'Zonnepanelen',
    'Elektrische Installatie',
    'HVAC Systemen',
    'Loodgieters',
    'Kraan Operators',
    'Stratenmakers & Grondwerkers'
  ];

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const data = await serviceAreaContentService?.getAllNews();
      setNews(data || []);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (newsItem = null) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        title: newsItem?.title,
        content: newsItem?.content,
        serviceArea: newsItem?.service_area,
        author: newsItem?.author,
        isActive: newsItem?.is_active
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        content: '',
        serviceArea: 'Zonnepanelen',
        author: 'BVM Group Redactie',
        isActive: true
      });
    }
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNews(null);
    setFormData({
      title: '',
      content: '',
      serviceArea: 'Zonnepanelen',
      author: 'BVM Group Redactie',
      isActive: true
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.title?.trim()) {
      newErrors.title = 'Titel is verplicht';
    }
    if (!formData?.content?.trim()) {
      newErrors.content = 'Inhoud is verplicht';
    }
    if (!formData?.serviceArea) {
      newErrors.serviceArea = 'Vakgebied is verplicht';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingNews) {
        await serviceAreaContentService?.updateNews(editingNews?.id, formData);
      } else {
        await serviceAreaContentService?.createNews(formData);
      }
      await loadNews();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving news:', error);
      setErrors({ submit: error?.message });
    }
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('Weet je zeker dat je dit nieuwsartikel wilt verwijderen?')) return;

    try {
      await serviceAreaContentService?.deleteNews(newsId);
      await loadNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Fout bij verwijderen: ' + error?.message);
    }
  };

  const filteredNews = filterServiceArea === 'all' 
    ? news 
    : news?.filter(n => n?.service_area === filterServiceArea);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Nieuws Beheer</h2>
          <p className="text-sm text-muted-foreground">Beheer nieuwsartikelen per vakgebied</p>
        </div>
        <Button onClick={() => handleOpenModal()} iconName="Plus">
          Nieuws Toevoegen
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-medium text-foreground">Filter op vakgebied:</label>
        <select
          value={filterServiceArea}
          onChange={(e) => setFilterServiceArea(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">Alle vakgebieden</option>
          {serviceAreas?.map(area => (
            <option key={area} value={area}>{area}</option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">
          ({filteredNews?.length} artikelen)
        </span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Laden...</div>
      ) : (
        <div 
          className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
          style={{
            boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Titel</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Vakgebied</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Auteur</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Datum</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredNews?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                      Geen nieuwsartikelen gevonden
                    </td>
                  </tr>
                ) : (
                  filteredNews?.map((newsItem) => (
                    <tr key={newsItem?.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm text-foreground font-medium">{newsItem?.title}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{newsItem?.service_area}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{newsItem?.author}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(newsItem?.published_date)?.toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          newsItem?.is_active 
                            ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                        }`}>
                          {newsItem?.is_active ? 'Actief' : 'Inactief'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Edit"
                            onClick={() => handleOpenModal(newsItem)}
                          >
                            Bewerken
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleDelete(newsItem?.id)}
                          >
                            Verwijderen
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-foreground">
                  {editingNews ? 'Nieuws Bewerken' : 'Nieuws Toevoegen'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Titel *
                  </label>
                  <Input
                    value={formData?.title}
                    onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                    placeholder="Titel van het nieuwsartikel"
                  />
                  {errors?.title && (
                    <p className="text-sm text-error mt-1">{errors?.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Inhoud *
                  </label>
                  <textarea
                    value={formData?.content}
                    onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
                    placeholder="Inhoud van het nieuwsartikel"
                    rows={6}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errors?.content && (
                    <p className="text-sm text-error mt-1">{errors?.content}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Vakgebied *
                  </label>
                  <select
                    value={formData?.serviceArea}
                    onChange={(e) => setFormData({ ...formData, serviceArea: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {serviceAreas?.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                  {errors?.serviceArea && (
                    <p className="text-sm text-error mt-1">{errors?.serviceArea}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Auteur
                  </label>
                  <Input
                    value={formData?.author}
                    onChange={(e) => setFormData({ ...formData, author: e?.target?.value })}
                    placeholder="Auteur naam"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData?.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <label className="text-sm text-foreground">Actief</label>
                </div>

                {errors?.submit && (
                  <p className="text-sm text-error">{errors?.submit}</p>
                )}

                <div className="flex items-center gap-3 pt-4">
                  <Button type="submit" variant="default">
                    {editingNews ? 'Bijwerken' : 'Toevoegen'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseModal}>
                    Annuleren
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;